/**
 * Live composer for the accounting view.
 *
 * Builds the same `AccountingView` payload the mock composer produces,
 * but from the org's real data: the `/views/activity` commit feed
 * (which already maps every commit to the org's business functions via
 * file-cluster classification — the heatmap `cells`), the real
 * `/engineers` roster, and the real `/business_functions` catalog.
 *
 * Cost is intentionally NOT computed here. This composer is salary-
 * independent and cached per-org; the per-commit `cost_usd` is filled
 * in afterwards by `applySalary` (see useAccountingView) so moving the
 * average-salary slider recomputes instantly without refetching or
 * reclassifying.
 *
 * See `classify.ts` for the investment-type / treatment heuristics and
 * the POC limitations (subject-driven signal; CapEx surfaced as
 * candidates by activity stage, not formally capitalized here).
 */

import type { BusinessFunction } from '@lib/api-types/business-functions';
import type { Engineer } from '@lib/api-types/engineers';
import { IdPrefix, toPublicId } from '@lib/api-types/ids';
import type { ActivityItem } from '@lib/api-types/views';

import { classifyCommit } from './classify';
import type { AccountingView, ClassifiedCommit } from './types';

const DAY = 24 * 60 * 60;
const DEFAULT_PERIOD_DAYS = 30;

/** Business function for commits whose files matched no cluster rule. */
const UNMAPPED_BF_ID = toPublicId(IdPrefix.business_function, 'unmapped');

export interface ComposeLiveInput {
  activityItems: ActivityItem[];
  engineers: Engineer[];
  businessFunctions: BusinessFunction[];
  /** Wall-clock "now" in unix epoch seconds — fallback period end when there are no commits. */
  now: number;
  periodDays?: number;
}

export interface LiveAccountingBase {
  view: AccountingView;
  /** Engineer ids the average salary applies to (active humans on the real roster). */
  salaried: string[];
  /** Period length in weeks — the salary proration denominator. */
  periodWeeks: number;
}

/** Parse the repo display slug out of a GitHub commit URL. */
function repoFromUrl(url: string | null): string {
  if (!url) return '';
  const m = /github\.com\/[^/]+\/([^/]+)\/commit\//.exec(url);
  return m?.[1] ?? '';
}

/** Pick the business function a commit rolls up to: the cell with the most weight. */
function dominantBusinessFunction(item: ActivityItem): string | null {
  if (item.cells.length === 0) return null;
  const byFunction = new Map<string, number>();
  for (const cell of item.cells) {
    byFunction.set(cell.function, (byFunction.get(cell.function) ?? 0) + cell.value);
  }
  let best: string | null = null;
  let bestValue = -1;
  for (const [fn, value] of byFunction) {
    if (value > bestValue) {
      best = fn;
      bestValue = value;
    }
  }
  return best;
}

function slugify(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'unknown';
}

export function composeLiveAccountingView(input: ComposeLiveInput): LiveAccountingBase {
  const periodDays = input.periodDays ?? DEFAULT_PERIOD_DAYS;
  // Anchor the window to the most recent commit, not wall-clock now. The
  // ingested history is often a backfill that ends months ago, so a
  // now-relative window would drop everything and render the view blank
  // (the original symptom for clowder-coffee). "Last 30 days" therefore
  // means the last 30 days of activity that exists. Falls back to now
  // only when there are no commits at all.
  const newestCommit = input.activityItems.reduce(
    (max, it) => (it.committed_at > max ? it.committed_at : max),
    0,
  );
  const periodEnd = newestCommit > 0 ? newestCommit : input.now;
  const periodStart = periodEnd - periodDays * DAY;
  const periodWeeks = periodDays / 7;

  // ── Lookups ──────────────────────────────────────────────────────────
  const bfById = new Map(input.businessFunctions.map((bf) => [bf.id, bf]));

  // login / name → engineer id, for author attribution. Lower-cased so
  // "RobertCraigie" and "robertcraigie" collapse.
  const engineerByLogin = new Map<string, string>();
  const engineerByName = new Map<string, string>();
  for (const e of input.engineers) {
    for (const login of e.github_logins) engineerByLogin.set(login.toLowerCase(), e.id);
    engineerByName.set(e.display_name.toLowerCase(), e.id);
  }

  // Synthetic rows for authors not on the roster — surfaced, not dropped.
  const syntheticEngineers = new Map<string, { id: string; display_name: string }>();

  function attribute(item: ActivityItem): string {
    const login = item.author_login?.toLowerCase();
    if (login && engineerByLogin.has(login)) return engineerByLogin.get(login)!;
    const name = item.author_name?.toLowerCase();
    if (name && engineerByName.has(name)) return engineerByName.get(name)!;
    const display = item.author_name ?? item.author_login ?? 'Unknown';
    const id = toPublicId(IdPrefix.engineer, `unattributed-${slugify(display)}`);
    if (!syntheticEngineers.has(id)) syntheticEngineers.set(id, { id, display_name: display });
    return id;
  }

  // ── Classify each in-window commit ───────────────────────────────────
  let anyUnmapped = false;
  const inWindow = input.activityItems.filter(
    (it) => it.committed_at >= periodStart && it.committed_at <= periodEnd,
  );

  const commits: ClassifiedCommit[] = inWindow.map((item) => {
    const fnId = dominantBusinessFunction(item);
    const resolvedFnId = fnId && bfById.has(fnId) ? fnId : UNMAPPED_BF_ID;
    if (resolvedFnId === UNMAPPED_BF_ID) anyUnmapped = true;
    const bfDisplay = bfById.get(resolvedFnId)?.display ?? null;

    const { suggestion, decision, flagged_for_review } = classifyCommit({
      subject: item.subject,
      businessFunctionDisplay: bfDisplay,
      committedAt: item.committed_at,
    });

    return {
      sha: item.sha,
      sha_short: item.sha_short,
      subject: item.subject,
      url: item.commit_url ?? '',
      author_engineer_id: attribute(item),
      committed_at: item.committed_at,
      additions: item.additions,
      deletions: item.deletions,
      files_changed: item.files_total,
      repo: repoFromUrl(item.commit_url),
      business_function_id: resolvedFnId,
      cost_usd: 0, // filled by applySalary
      suggestion,
      decision,
      flagged_for_review,
    };
  });

  // ── Axes ─────────────────────────────────────────────────────────────
  const businessFunctions: AccountingView['business_functions'] = input.businessFunctions.map(
    (bf) => ({ id: bf.id, display: bf.display, subtitle: bf.subtitle ?? null }),
  );
  if (anyUnmapped) {
    businessFunctions.push({
      id: UNMAPPED_BF_ID,
      display: 'Unmapped',
      subtitle: 'Files did not match any business-function rule',
    });
  }

  const engineers: AccountingView['engineers'] = [
    ...input.engineers.map((e) => ({
      id: e.id,
      display_name: e.display_name,
      kind: e.kind,
      operator_engineer_id: null,
    })),
    ...Array.from(syntheticEngineers.values()).map((e) => ({
      id: e.id,
      display_name: e.display_name,
      kind: 'human' as const,
      operator_engineer_id: null,
    })),
  ];

  const salaried = input.engineers
    .filter((e) => e.kind === 'human' && e.status === 'active')
    .map((e) => e.id);

  const view: AccountingView = {
    object: 'accounting_view',
    branch_id: 'branch_live',
    generated_at: input.now,
    period_start: periodStart,
    period_end: periodEnd,
    poc_disclaimer: '', // set by applySalary (carries the salary figure)
    business_functions: businessFunctions,
    engineers,
    accounting_projects: [], // CapEx candidates carry no linked project — formal capitalization happens in the close
    commits,
  };

  return { view, salaried, periodWeeks };
}

/**
 * Apply an average salary to a (salary-independent) base, returning the
 * finished view. Per spec §"Cost calculation": each human's period cost
 * is `salary / 52 × period_weeks`, spread evenly across that engineer's
 * commits in the window (MVP weights every commit equally). Agents and
 * unrecognized authors carry no labor cost.
 *
 * Pure + cheap, so the hook can re-run it on every salary change over a
 * cached base instead of refetching.
 */
export function applySalary(base: LiveAccountingBase, salaryUsd: number): AccountingView {
  const salaried = new Set(base.salaried);
  const commitsByEngineer = new Map<string, number>();
  for (const c of base.view.commits) {
    if (salaried.has(c.author_engineer_id)) {
      commitsByEngineer.set(
        c.author_engineer_id,
        (commitsByEngineer.get(c.author_engineer_id) ?? 0) + 1,
      );
    }
  }
  const periodSalaryCost = (salaryUsd / 52) * base.periodWeeks;

  const commits = base.view.commits.map((c) => {
    if (!salaried.has(c.author_engineer_id)) return { ...c, cost_usd: 0 };
    const n = commitsByEngineer.get(c.author_engineer_id) ?? 1;
    return { ...c, cost_usd: Math.round(periodSalaryCost / n) };
  });

  return {
    ...base.view,
    commits,
    poc_disclaimer: POC_DISCLAIMER,
  };
}

// Fixed for the POC — the salary control still drives the cost numbers, but
// the banner copy stays pinned at $200k.
const POC_DISCLAIMER =
  'POC: for demo purposes only. Capital allocation of last 30 days of ' +
  'commit history modelled on $200k unloaded salary for every engineer.';
